import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  Layers,
  Calendar,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { ScreenHelpDrawer } from '@/components/common/ScreenHelpDrawer';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { LoadingState } from '@/components/common/LoadingState';
import { Button } from '@/components/ui/button';
import {
  reportService,
  CyclePerformanceData,
  RaiserPerformanceData,
  ManagementSummaryData,
  MonthlyFinancialActivityRecord,
  AnnualFinancialSummaryRecord,
  ReportFilters,
  filterCyclePerformanceData,
  computeManagementSummary,
  computeMonthlyFinancialActivity,
  aggregatePerformanceByRaiser,
} from '@/services/reportService';
import { GeneralLedgerEntry } from '@/services/ledgerService';
import { ManagementSummaryView } from '@/components/reports/ManagementSummaryView';
import { ProductionPerformanceView } from '@/components/reports/ProductionPerformanceView';
import { MonthlyFinancialActivityView } from '@/components/reports/MonthlyFinancialActivityView';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { YearSelector } from '@/components/reports/YearSelector';
import { ExportDropdown } from '@/components/reports/ExportDropdown';
import { ReportPrintModal, ReportPrintMode } from '@/components/reports/ReportPrintModal';

type ActiveReportView = 'summary' | 'performance' | 'monthly';

export const ReportsPage: React.FC = () => {
  const [activeView, setActiveView] = useState<ActiveReportView>('summary');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // PDF Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [printReportMode, setPrintReportMode] = useState<ReportPrintMode>('current_summary');

  // Global Reporting Year state (defaults to latest year with data, e.g. '2026')
  const [availableYears, setAvailableYears] = useState<string[]>(['2026', '2025', '2024']);
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Raw dataset states
  const [rawCycles, setRawCycles] = useState<CyclePerformanceData[]>([]);
  const [rawLedgerEntries, setRawLedgerEntries] = useState<GeneralLedgerEntry[]>([]);
  const [annualSummary, setAnnualSummary] = useState<AnnualFinancialSummaryRecord[]>([]);
  const [rawSettlements, setRawSettlements] = useState<any[]>([]);
  const [rawIssues, setRawIssues] = useState<any[]>([]);

  // Sub-filters for Production Performance
  const [filters, setFilters] = useState<ReportFilters>({
    batchCode: 'all',
    farmId: 'all',
    growerId: 'all',
    status: 'all',
    search: '',
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportService.fetchCompleteReportsDataset();
      setRawCycles(res.cyclesPerformance);
      setRawLedgerEntries(res.ledgerEntries);
      setAnnualSummary(res.annualFinancialSummary || []);
      setRawSettlements(res.settlements || []);
      setRawIssues(res.reconciliationIssues || []);
      if (res.availableYears && res.availableYears.length > 0) {
        setAvailableYears(res.availableYears);
        // Only set default if selectedYear is not yet in availableYears
        if (!res.availableYears.includes(selectedYear) && selectedYear !== 'all') {
          setSelectedYear(res.defaultYear || res.availableYears[0]);
        }
      }
    } catch (err: any) {
      console.error('[ReportsPage] Error loading reports dataset:', err);
      setError(err.message || 'Failed to load report analytics dataset from database.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 1. Reactive Management Summary for Selected Year
  const managementSummary = useMemo(() => {
    return computeManagementSummary(rawCycles, rawLedgerEntries, selectedYear, rawSettlements, rawIssues);
  }, [rawCycles, rawLedgerEntries, selectedYear, rawSettlements, rawIssues]);

  // 2. Reactive Monthly Financial Activity for Selected Year
  const monthlyRecords = useMemo(() => {
    return computeMonthlyFinancialActivity(rawLedgerEntries, selectedYear);
  }, [rawLedgerEntries, selectedYear]);

  // 3. Filtered Cycles respecting selected Reporting Year and sub-filters
  const filteredCycles = useMemo(() => {
    return filterCyclePerformanceData(rawCycles, {
      ...filters,
      selectedYear,
    });
  }, [rawCycles, filters, selectedYear]);

  // 4. Raiser Performance aggregated across cycles in selected Reporting Year
  const raiserPerformanceForYear = useMemo(() => {
    const cyclesInYear = filterCyclePerformanceData(rawCycles, { selectedYear });
    return aggregatePerformanceByRaiser(cyclesInYear);
  }, [rawCycles, selectedYear]);

  // 5. Available Batches restricted to Selected Year
  const availableBatches = useMemo(() => {
    const set = new Set<string>();
    const cyclesInYear = filterCyclePerformanceData(rawCycles, { selectedYear });
    for (const c of cyclesInYear) {
      if (c.batchCode) set.add(c.batchCode);
    }
    return Array.from(set).sort();
  }, [rawCycles, selectedYear]);

  // 6. Available Farms
  const availableFarms = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of rawCycles) {
      if (c.farmId && c.farmName) {
        map.set(c.farmId, c.farmName);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rawCycles]);

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Top Header & Navigation Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-slate-200">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-700" />
            Reports & Operational Analytics
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Management overview, cycle/raiser performance, and monthly financial activity.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <ExportDropdown
            data={filteredCycles}
            activeViewTitle={
              activeView === 'summary'
                ? 'Management Summary'
                : activeView === 'performance'
                ? 'Production Performance'
                : 'Monthly Financials'
            }
            onPrintCurrentView={() => {
              setPrintReportMode(
                activeView === 'summary'
                  ? 'current_summary'
                  : activeView === 'performance'
                  ? 'current_performance'
                  : 'current_monthly'
              );
              setIsPrintModalOpen(true);
            }}
            onPrintManagementPack={() => {
              setPrintReportMode('management_pack');
              setIsPrintModalOpen(true);
            }}
          />
          <ScreenHelpDrawer screenKey="reports" />
        </div>
      </div>

      {/* 2. Error Banner with Retry */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3 text-red-800 text-sm">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-semibold">Unable to load reporting dataset</p>
              <p className="text-xs text-red-700 mt-0.5">{error}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="bg-white hover:bg-red-50 text-red-700 border-red-300 text-xs font-semibold"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Retry
          </Button>
        </div>
      )}

      {/* 3. Primary Navigation Bar with View Switcher & Global Year Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-2 sm:p-2.5 border border-slate-200 rounded-2xl shadow-sm">
        {/* 3-View Tab Switcher */}
        <div className="grid grid-cols-3 sm:flex sm:items-center gap-1 p-1 bg-slate-100 rounded-xl w-full sm:w-fit shadow-inner">
          <ActionTooltip content="Executive overview of booked financials, live swine inventory, settlements, and attention items.">
            <button
              onClick={() => setActiveView('summary')}
              className={`min-h-[44px] px-2 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-center ${
                activeView === 'summary'
                  ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
              }`}
            >
              <TrendingUp className="w-4 h-4 text-emerald-700 shrink-0" />
              <span className="hidden sm:inline">Management Summary</span>
              <span className="sm:hidden">Summary</span>
            </button>
          </ActionTooltip>

          <ActionTooltip content="Cohort analysis of sales, feed costs, gross profit, and FCR by cycle and raiser.">
            <button
              onClick={() => setActiveView('performance')}
              className={`min-h-[44px] px-2 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-center ${
                activeView === 'performance'
                  ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
              }`}
            >
              <Layers className="w-4 h-4 text-indigo-700 shrink-0" />
              <span className="hidden sm:inline">Production Performance</span>
              <span className="sm:hidden">Performance</span>
            </button>
          </ActionTooltip>

          <ActionTooltip content="Monthly calendar cashflow ledger breakdown with prior-year comparisons.">
            <button
              onClick={() => setActiveView('monthly')}
              className={`min-h-[44px] px-2 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-center ${
                activeView === 'monthly'
                  ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
              }`}
            >
              <Calendar className="w-4 h-4 text-blue-700 shrink-0" />
              <span className="hidden sm:inline">Monthly Financial Activity</span>
              <span className="sm:hidden">Financials</span>
            </button>
          </ActionTooltip>
        </div>

        {/* Global Reporting Year Selector */}
        <div className="flex items-center justify-between sm:justify-end w-full md:w-auto">
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={(y) => {
              setSelectedYear(y);
              // Reset batch filter if it is no longer valid in new year
              setFilters((prev) => ({ ...prev, batchCode: 'all' }));
            }}
          />
        </div>
      </div>

      {/* 4. Global Sub-Filter Bar (Active in Performance View) */}
      {activeView === 'performance' && (
        <ReportFilterBar
          filters={filters}
          onFilterChange={setFilters}
          availableBatches={availableBatches}
          availableFarms={availableFarms}
        />
      )}

      {/* 5. View Contents */}
      {loading ? (
        <LoadingState message="Aggregating live operational dataset and ledger records..." />
      ) : (
        <>
          {activeView === 'summary' && (
            <ManagementSummaryView summary={managementSummary} />
          )}

          {activeView === 'performance' && (
            <ProductionPerformanceView
              cycles={filteredCycles}
              raisers={raiserPerformanceForYear}
              selectedYear={selectedYear}
            />
          )}

          {activeView === 'monthly' && (
            <MonthlyFinancialActivityView
              records={monthlyRecords}
              selectedYear={selectedYear}
              annualSummary={annualSummary}
            />
          )}
        </>
      )}

      {/* PDF Print / Export Preview Modal */}
      {isPrintModalOpen && (
        <ReportPrintModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          reportMode={printReportMode}
          selectedYear={selectedYear}
          summary={managementSummary}
          filteredCycles={filteredCycles}
          raiserPerformance={raiserPerformanceForYear}
          monthlyRecords={monthlyRecords}
          annualSummary={annualSummary}
          filters={filters}
          availableFarms={availableFarms}
        />
      )}
    </div>
  );
};

